/**
 * useResponsiveScaleSharedValues
 *
 * Custom hook that manages SharedValue versions of responsive scale factors
 * using React Native Reanimated. This hook is designed to work independently
 * from React's rendering cycle and will eventually replace the useMemo-based
 * approach in ResponsiveScaleProvider.
 *
 * The hook uses useAnimatedReaction to automatically update SharedValues
 * when input dependencies change, providing smooth animations without
 * causing React re-renders.
 */
import { useMemo, useState, useLayoutEffect } from 'react';
import { useSharedValue, useAnimatedReaction, useDerivedValue, runOnJS, SharedValue } from 'react-native-reanimated';
import { useSizeLimitContext } from '../contexts/SizeLimitContext';

/**
 * Interface for the hook's input parameters
 */
interface UseResponsiveScaleSharedValuesProps {
  viewBox: { width: number; height: number };
  containerViewBox?: { width: number; height: number };
  containerDimensionsSV?: SharedValue<{ width: number; height: number }>;
}

/**
 * Interface for the hook's return value
 * Extended to include React values derived from SharedValues
 */
interface UseResponsiveScaleSharedValuesReturn {
  responsiveScaleSV: ReturnType<typeof useSharedValue<number>>;
  containerResponsiveScaleSV: ReturnType<typeof useSharedValue<number>>;
  responsiveScale: number;
  containerResponsiveScale: number;
}

/**
 * Calculates the scale factor needed to fit an element within a container
 * while preserving aspect ratio.
 *
 * This is the same pure function used in ResponsiveScaleProvider
 * to ensure consistent calculations between React values and SharedValues.
 */
const calculateScaleFrom = (
  container: { height: number; width: number },
  element: { height: number; width: number },
) => {
  // Calculate width based on container height while maintaining aspect ratio
  let actualViewBoxWidth = (element.width * container.height) / element.height;
  // If calculated width exceeds container width, constrain to container width
  if (actualViewBoxWidth > container.width) actualViewBoxWidth = container.width;
  // Calculate and return the scale factor
  return actualViewBoxWidth / element.width;
};

/**
 * Custom hook that creates and manages SharedValue versions of responsive scale factors
 *
 * @param viewBox - The dimensions of the content that needs to be scaled
 * @param containerViewBox - Optional outer viewBox dimensions for nested scaling scenarios
 * @returns Object containing responsiveScaleSV and containerResponsiveScaleSV SharedValues
 */
export const useResponsiveScaleSharedValues = ({
  viewBox,
  containerViewBox,
  containerDimensionsSV,
}: UseResponsiveScaleSharedValuesProps): UseResponsiveScaleSharedValuesReturn => {
  // Get current window/container size from context (fallback) or direct SharedValue
  const windowSize = useSizeLimitContext();

  // Stabilize viewBox and containerViewBox references to prevent unnecessary re-executions
  const stableViewBox = useMemo(() => viewBox, [viewBox.width, viewBox.height]);
  const stableContainerViewBox = useMemo(() => containerViewBox, [containerViewBox?.width, containerViewBox?.height]);

  // Calculate initial values to avoid starting from 0
  const calculateInitialValues = () => {
    let containerViewBoxResponsiveScale = 0;
    let viewBoxResponsiveScale = 0;

    if (stableContainerViewBox) {
      containerViewBoxResponsiveScale = calculateScaleFrom(windowSize, stableContainerViewBox);
      const scaledContainerViewBox = {
        width: stableContainerViewBox.width * containerViewBoxResponsiveScale,
        height: stableContainerViewBox.height * containerViewBoxResponsiveScale,
      };
      viewBoxResponsiveScale = calculateScaleFrom(scaledContainerViewBox, stableViewBox);
    } else {
      viewBoxResponsiveScale = calculateScaleFrom(windowSize, stableViewBox);
    }

    return { containerViewBoxResponsiveScale, viewBoxResponsiveScale };
  };

  const initialValues = calculateInitialValues();

  // Initialize SharedValues with correct initial values
  const responsiveScaleSV = useSharedValue<number>(initialValues.viewBoxResponsiveScale);
  const containerResponsiveScaleSV = useSharedValue<number>(initialValues.containerViewBoxResponsiveScale);

  // React state derived from SharedValues - single source of truth
  const [responsiveScale, setResponsiveScale] = useState<number>(initialValues.viewBoxResponsiveScale);
  const [containerResponsiveScale, setContainerResponsiveScale] = useState<number>(
    initialValues.containerViewBoxResponsiveScale,
  );

  // Derive React values from SharedValues with synchronization
  useDerivedValue(() => {
    runOnJS(setResponsiveScale)(responsiveScaleSV.value);
  }, [responsiveScaleSV]);

  useDerivedValue(() => {
    runOnJS(setContainerResponsiveScale)(containerResponsiveScaleSV.value);
  }, [containerResponsiveScaleSV]);

  // Synchronize React values immediately when SharedValues change (for faster updates)
  useLayoutEffect(() => {
    setResponsiveScale(responsiveScaleSV.value);
    setContainerResponsiveScale(containerResponsiveScaleSV.value);
  }, [responsiveScaleSV.value, containerResponsiveScaleSV.value]);

  // Use animated reaction to update SharedValues when dependencies change
  // Optimized with support for direct container dimensions and significant change detection
  useAnimatedReaction(
    () => {
      // Use direct container dimensions if available, fallback to context
      const containerDims = containerDimensionsSV?.value || windowSize;

      return {
        windowWidth: containerDims.width,
        windowHeight: containerDims.height,
        viewBoxWidth: stableViewBox.width,
        viewBoxHeight: stableViewBox.height,
        containerViewBoxWidth: stableContainerViewBox?.width ?? 0,
        containerViewBoxHeight: stableContainerViewBox?.height ?? 0,
      };
    },
    (current, previous) => {
      // Skip calculation if no significant change (performance optimization)
      if (previous) {
        const THRESHOLD = 0.001; // Ignore changes smaller than 0.1%
        const hasSignificantChange =
          Math.abs(current.windowWidth - previous.windowWidth) > THRESHOLD ||
          Math.abs(current.windowHeight - previous.windowHeight) > THRESHOLD ||
          Math.abs(current.viewBoxWidth - previous.viewBoxWidth) > THRESHOLD ||
          Math.abs(current.viewBoxHeight - previous.viewBoxHeight) > THRESHOLD ||
          Math.abs(current.containerViewBoxWidth - previous.containerViewBoxWidth) > THRESHOLD ||
          Math.abs(current.containerViewBoxHeight - previous.containerViewBoxHeight) > THRESHOLD;

        if (!hasSignificantChange) {
          return; // Skip calculation for insignificant changes
        }
      }

      // Initialize scale factors
      let containerViewBoxResponsiveScale = 0;
      let viewBoxResponsiveScale = 0;

      // Handle two different scaling scenarios (same logic as ResponsiveScaleProvider)
      if (stableContainerViewBox) {
        // Calculate scale for container if provided
        containerViewBoxResponsiveScale = calculateScaleFrom(
          { width: current.windowWidth, height: current.windowHeight },
          { width: current.containerViewBoxWidth, height: current.containerViewBoxHeight },
        );

        // Scenario 1: Nested scaling - content is within another scaled container
        // First calculate the scaled container dimensions
        const scaledContainerViewBox = {
          width: current.containerViewBoxWidth * containerViewBoxResponsiveScale,
          height: current.containerViewBoxHeight * containerViewBoxResponsiveScale,
        };
        // Then scale content within the scaled container
        viewBoxResponsiveScale = calculateScaleFrom(scaledContainerViewBox, {
          width: current.viewBoxWidth,
          height: current.viewBoxHeight,
        });
      } else {
        // Scenario 2: Direct scaling - content scales directly to window size
        viewBoxResponsiveScale = calculateScaleFrom(
          { width: current.windowWidth, height: current.windowHeight },
          { width: current.viewBoxWidth, height: current.viewBoxHeight },
        );
      }

      // Update SharedValues atomically to prevent inconsistent states
      containerResponsiveScaleSV.value = containerViewBoxResponsiveScale;
      responsiveScaleSV.value = viewBoxResponsiveScale;
    },
    [
      windowSize.width,
      windowSize.height,
      viewBox.width,
      viewBox.height,
      containerViewBox?.width,
      containerViewBox?.height,
      containerDimensionsSV,
    ],
  );

  return {
    responsiveScaleSV,
    containerResponsiveScaleSV,
    responsiveScale,
    containerResponsiveScale,
  };
};
